import Box from '@mui/material/Box';
import { Styles } from '@t2c/shared-fe';
import React, { Fragment, useState } from 'react';
import { useBrollCaption, useDialog } from '../../hooks';
import Stack from '@mui/material/Stack';
import { CircularProgress, Typography } from '@mui/material';
import {
  BrollCaptionsAndRenderType,
  ContentType,
  EMPTY_PAGE_TEXT,
  FilteringSortingParams,
  MediaStatus,
  PAGE_DATA_LIMIT,
} from '../../../t2c-fe/utils';
import EmptyPage from '../../../t2c-fe/Common/EmptyPage';
import { useRouter } from 'next/router';
import { Button } from '@mui/material';
import { Grid } from '@mui/material';
import { BrollCaptionCard } from '../broll-caption/card/BrollCaptionCard';
import { FiltersAndSorting } from '../../../t2c-fe/Common/FiltersAndSorting';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Props {
  title?: string;
  refetchById?: () => void;
  allData?: boolean;
  isTimelinePage?: boolean;
}

export const BrollVideosSection: React.FC<Props> = ({ title, refetchById, allData, isTimelinePage=false }) => {
  const [params, setParams] = useState<FilteringSortingParams>({
    sortOrder: 'desc',
    limit: allData ? PAGE_DATA_LIMIT : 4,
    sortBy: 'updatedAt',
  });

  const endpoint = isTimelinePage ? 'clipdeck' : 'broll-caption'

  const {
    brollCaptions,
    isBrollCaptionsLoading,
    refetchDataBrollCaptions,
    fetchNextPage,
    hasNextPage,
  } = useBrollCaption(params, isTimelinePage && BrollCaptionsAndRenderType.CLIPDECK);

  const refetch = () => {
    refetchById && refetchById();
    refetchDataBrollCaptions();
  };
  const { push } = useRouter();

  const handlePreview = (id) => {
    push(`/${endpoint}/${id}`);
  };

  const handleViewAll = () => {
    push(`/${endpoint}`);
  };

  const handleVideoClick = (id: string) => {
    push(`/clipdeck/editor?id=${id}`);
  };

  const filterOptions = [
    {
      id: 1,
      name: 'In queue',
      value: MediaStatus.NotStarted,
    },
    {
      id: 2,
      name: 'Generating',
      value: MediaStatus.InProgress,
    },
    {
      id: 3,
      name: 'Generated',
      value: MediaStatus.Success,
    },
    {
      id: 4,
      name: 'Failed',
      value: MediaStatus.Error,
    },
  ];

  const handleFilterOptionChange = (val: MediaStatus) => {
    setParams({ ...params, filters: { status: val } });
  };

  return (
    <Stack rowGap={3} pb={2} mt={4}>
      {!allData && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{title ? title : 'Captions & Effects Videos'}</Typography>
          <Button onClick={handleViewAll}>View All</Button>
        </Box>
      )}

      {allData && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <FiltersAndSorting
            params={params}
            setParams={setParams}
            filterArray={filterOptions}
            handleFilterOptionChange={handleFilterOptionChange}
          />
        </Box>
      )}

      {isBrollCaptionsLoading ? (
        <Stack sx={styles.loader}>
          <CircularProgress />
        </Stack>
      ) : !(brollCaptions?.pages?.[0]?.brollCaptionVideos ?? []).length &&
        !isBrollCaptionsLoading ? (
        <EmptyPage text={EMPTY_PAGE_TEXT.video} type={ContentType.Video} />
      ) : (
        <Box>
          {allData ? (
            <InfiniteScroll
              dataLength={
                brollCaptions?.pages?.reduce(
                  (acc, page) => acc + page.brollCaptionVideos?.length,
                  0
                ) || 0
              }
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                <Box p={2} textAlign={'center'}>
                  <h2>loading...</h2>
                </Box>
              }
            >
              <Grid container spacing={4}>
                {brollCaptions?.pages?.map((page, pageIndex) => (
                  <Fragment key={pageIndex}>
                    {page?.brollCaptionVideos?.map((res) => (
                      <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                        <BrollCaptionCard
                          data={res}
                          refreshBrollData={refetch}
                          handleOnPreview={handlePreview}
                          {...(isTimelinePage && { handleEdit: handleVideoClick })}
                        />
                      </Grid>
                    ))}
                  </Fragment>
                ))}
              </Grid>
            </InfiniteScroll>
          ) : (
            <Grid container spacing={4}>
              {brollCaptions?.pages?.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {page?.brollCaptionVideos?.map((res) => (
                    <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                      <BrollCaptionCard
                        data={res}
                        refreshBrollData={refetch}
                        handleOnPreview={handlePreview}
                        {...(isTimelinePage && { handleEdit: handleVideoClick })}
                      />
                    </Grid>
                  ))}
                </Fragment>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Stack>
  );
};

const styles: Styles = {
  cardContainer: {
    minHeight: 180,
  },
  card: {
    width: 360,
  },
  loader: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20%',
  },
};
